import React, { useState, useEffect, useRef } from 'react';
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TrendingUp, TrendingDown, Minus, DollarSign, Eye, BarChart3 } from 'lucide-react';

/**
 * Minimalist Trend Chart using SVG
 * Shows 30-day rolling data with tooltip on hover
 */
const MiniLineChart = ({ data, color = "#6366f1", height = 60, formatValue, unit = "" }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const svgRef = useRef(null);
  
  if (!data || data.length === 0) {
    return <div className="h-[60px] flex items-center justify-center text-slate-500 text-xs">No data</div>;
  }
  
  const width = 200;
  const padding = { top: 5, bottom: 5, left: 5, right: 5 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Calculate min/max for scaling
  const values = data.map(d => d.value);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;
  
  // Scale functions
  const xScale = (i) => padding.left + (i / (data.length - 1)) * chartWidth;
  const yScale = (v) => padding.top + chartHeight - ((v - minVal) / range) * chartHeight;
  
  // Build path
  const pathD = data.map((d, i) => {
    const x = xScale(i);
    const y = yScale(d.value);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
  
  // Build area path (for gradient fill)
  const areaD = pathD + ` L ${xScale(data.length - 1)} ${padding.top + chartHeight} L ${padding.left} ${padding.top + chartHeight} Z`;
  
  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - padding.left;
    const index = Math.round((x / chartWidth) * (data.length - 1));
    if (index >= 0 && index < data.length) {
      setHoveredPoint({ index, ...data[index] });
    }
  };
  
  return (
    <div className="relative">
      <svg 
        ref={svgRef}
        width={width} 
        height={height} 
        className="cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPoint(null)}
      >
        <defs>
          <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        
        {/* Area fill */}
        <path 
          d={areaD} 
          fill={`url(#gradient-${color.replace('#', '')})`}
        />
        
        {/* Line */}
        <path 
          d={pathD} 
          fill="none" 
          stroke={color} 
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Hover point */}
        {hoveredPoint && (
          <>
            <circle 
              cx={xScale(hoveredPoint.index)} 
              cy={yScale(hoveredPoint.value)} 
              r="4" 
              fill={color}
              stroke="white"
              strokeWidth="2"
            />
            <line
              x1={xScale(hoveredPoint.index)}
              y1={padding.top}
              x2={xScale(hoveredPoint.index)}
              y2={padding.top + chartHeight}
              stroke={color}
              strokeWidth="1"
              strokeDasharray="2,2"
              opacity="0.5"
            />
          </>
        )}
      </svg>
      
      {/* Tooltip */}
      {hoveredPoint && (
        <div 
          className="absolute bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs pointer-events-none z-10"
          style={{
            left: Math.min(xScale(hoveredPoint.index), width - 80),
            top: -30,
          }}
        >
          <div className="text-slate-400">{hoveredPoint.date}</div>
          <div className="text-white font-medium">
            {formatValue ? formatValue(hoveredPoint.value) : hoveredPoint.value}{unit}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Single Trend Card Component
 */
const TrendCard = ({ title, icon: Icon, data, current, average, average7d, momentum, color, formatValue, unit, isPercentage }) => {
  const getMomentumLabel = () => {
    if (momentum === 'up') return 'Improving';
    if (momentum === 'down') return 'Declining';
    return 'Stable';
  };

  const getMomentumIcon = () => {
    if (momentum === 'up') return <TrendingUp className="h-3 w-3" />;
    if (momentum === 'down') return <TrendingDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  const getMomentumColor = () => {
    if (momentum === 'up') return 'text-emerald-400';
    if (momentum === 'down') return 'text-red-400';
    return 'text-slate-400';
  };
  
  return (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Icon className="h-3.5 w-3.5" />
          <span>{title}</span>
        </div>
        <div className={`flex items-center gap-1 ${getMomentumColor()}`}>
          {getMomentumIcon()}
        </div>
      </div>
      
      <MiniLineChart 
        data={data} 
        color={color} 
        formatValue={formatValue}
        unit={unit}
      />
      
      <div className="flex justify-between items-end mt-2 text-xs">
        <div>
          <div className="text-slate-500">Current</div>
          <div className="text-white font-medium">
            {isPercentage ? `${current}%` : formatValue ? formatValue(current) : current}
          </div>
        </div>
        <div className="text-right">
          <div className="text-slate-500">30d Avg</div>
          <div className="text-slate-300">
            {isPercentage ? `${average}%` : formatValue ? formatValue(average) : average}
          </div>
        </div>
      </div>
      
      {/* Momentum Label */}
      <div className={`flex items-center gap-1 mt-2 pt-2 border-t border-white/5 text-xs ${getMomentumColor()}`}>
        {getMomentumIcon()}
        <span>Momentum: {getMomentumLabel()}</span>
      </div>
    </div>
  );
};

/**
 * 30-Day Rolling Trend Charts
 * Displays trend lines for Revenue/Lead, Show Rate, Sales Conversion
 * MA-IMPL-118B: Added campaignId filter support
 */
const TrendCharts = ({ vertical = "all", campaignId, className = "" }) => {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (vertical && vertical !== "all") {
          params.append("vertical", vertical);
        }
        // MA-IMPL-118B: Include campaign filter
        if (campaignId && campaignId !== "all") {
          params.append("campaign_id", campaignId);
        }
        const res = await api.get(`/analytics/trends?${params.toString()}`);
        setTrends(res.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load trends');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrends();
  }, [vertical, campaignId]);

  if (loading) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`} data-testid="trend-charts">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white/5 rounded-lg p-3 border border-white/10">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-[60px] w-full mb-2" />
            <div className="flex justify-between">
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-xs text-red-400 ${className}`}>
        {error}
      </div>
    );
  }

  if (!trends) return null;

  const formatCurrency = (val) => {
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
    return `$${val.toFixed(0)}`;
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`} data-testid="trend-charts">
      <TrendCard
        title="Revenue / Lead"
        icon={DollarSign}
        data={trends.revenue_per_lead?.data || []}
        current={trends.revenue_per_lead?.current || 0}
        average={trends.revenue_per_lead?.average_30d || trends.revenue_per_lead?.average || 0}
        average7d={trends.revenue_per_lead?.average_7d || 0}
        momentum={trends.revenue_per_lead?.momentum || "flat"}
        color="#10b981"
        formatValue={formatCurrency}
        unit=""
        isPercentage={false}
      />
      
      <TrendCard
        title="Show Rate"
        icon={Eye}
        data={trends.show_rate?.data || []}
        current={trends.show_rate?.current || 0}
        average={trends.show_rate?.average_30d || trends.show_rate?.average || 0}
        average7d={trends.show_rate?.average_7d || 0}
        momentum={trends.show_rate?.momentum || "flat"}
        color="#6366f1"
        unit="%"
        isPercentage={true}
      />
      
      <TrendCard
        title="Sales Conversion"
        icon={BarChart3}
        data={trends.sales_conversion_rate?.data || []}
        current={trends.sales_conversion_rate?.current || 0}
        average={trends.sales_conversion_rate?.average_30d || trends.sales_conversion_rate?.average || 0}
        average7d={trends.sales_conversion_rate?.average_7d || 0}
        momentum={trends.sales_conversion_rate?.momentum || "flat"}
        color="#f59e0b"
        unit="%"
        isPercentage={true}
      />
    </div>
  );
};

export default TrendCharts;
